import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  Settings,
  Building2,
  Shield,
  Megaphone,
  Crown,
  Database,
  Save,
  Upload,
  Trash2,
  User,
  Camera,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Phone,
  Lock,
  Mail,
  Eye,
  EyeOff,
  Bell,
} from "lucide-react";
import NotificationPreferencesPanel from "../components/NotificationPreferencesPanel";

// ─── Types ───────────────────────────────────────────────────────────────────

type Club = {
  id: string;
  name: string;
  city: string;
  country: string;
  description: string;
  logo_path: string;
  primary_color: string;
  is_premium: boolean;
};

type ClubSettingsState = {
  coach_can_invite: boolean;
  player_can_see_others_stats: boolean;
  parent_can_see_team_stats: boolean;
  player_can_post: boolean;
  posts_allow_coaches: boolean;
  posts_allow_players: boolean;
  posts_allow_parents: boolean;
};

type ProfileForm = {
  full_name: string;
  club: string;
  niveau: string;
  pseudo: string;
  public_use_pseudo: boolean;
  photo_url: string;
  phone: string;
};

type TabKey = "general" | "permissions" | "posts" | "billing" | "data" | "profil" | "notifications";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

const isValidPseudo = (v: string) => /^[A-Za-z0-9_.-]{3,32}$/.test(v);

function getStorageUrl(bucket: string, path: string) {
  if (!path) return "";
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}

const EDITABLE_ROLES = ["dirigeant", "cadre_technique"];

const DEFAULT_SETTINGS: ClubSettingsState = {
  coach_can_invite: false,
  player_can_see_others_stats: true,
  parent_can_see_team_stats: true,
  player_can_post: false,
  posts_allow_coaches: true,
  posts_allow_players: false,
  posts_allow_parents: false,
};

// ─── Toggle ───────────────────────────────────────────────────────────────────

function Toggle({
  label,
  desc,
  value,
  onChange,
  disabled,
}: {
  label: string;
  desc?: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl border border-gray-200 dark:border-gray-800 p-4">
      <div className="min-w-0">
        <div className="font-semibold text-gray-900 dark:text-gray-100">{label}</div>
        {desc && <div className="text-sm text-gray-500 dark:text-gray-400">{desc}</div>}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(!value)}
        className={cx(
          "relative inline-flex h-6 w-11 items-center rounded-full transition shrink-0",
          value ? "bg-gray-900 dark:bg-white" : "bg-gray-300 dark:bg-gray-700",
          disabled && "opacity-60 cursor-not-allowed"
        )}
        aria-label={label}
      >
        <span
          className={cx(
            "inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-900 transition",
            value ? "translate-x-5" : "translate-x-1"
          )}
        />
      </button>
    </div>
  );
}

// ─── Profile Tab ──────────────────────────────────────────────────────────────

function ProfileTab() {
  const [form, setForm] = useState<ProfileForm>({
    full_name: "",
    club: "",
    niveau: "",
    pseudo: "",
    public_use_pseudo: false,
    photo_url: "",
    phone: "",
  });
  const [userEmail, setUserEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [pseudoErr, setPseudoErr] = useState("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [userId, setUserId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Password change state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState("");
  const [pwdSuccess, setPwdSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const { data: { user }, error: authErr } = await supabase.auth.getUser();
        if (authErr) throw authErr;
        if (!user) throw new Error("Utilisateur non connecté.");

        setUserId(user.id);
        setUserEmail(user.email ?? "");

        const { data, error: profileErr } = await supabase
          .from("user_profiles")
          .select("full_name, club, niveau, pseudo, public_use_pseudo, photo_url, phone")
          .eq("id", user.id)
          .maybeSingle();

        if (profileErr) throw profileErr;

        if (!cancelled && data) {
          setForm({
            full_name: data.full_name ?? "",
            club: data.club ?? "",
            niveau: data.niveau ?? "",
            pseudo: data.pseudo ?? "",
            public_use_pseudo: data.public_use_pseudo ?? false,
            photo_url: data.photo_url ?? "",
            phone: data.phone ?? "",
          });
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Impossible de charger le profil.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  async function handleSave() {
    setPseudoErr("");
    setError("");
    setSuccess(false);

    if (!form.full_name.trim()) { setError("Le nom complet est requis."); return; }
    if (form.public_use_pseudo && !form.pseudo.trim()) { setPseudoErr("Renseigne un pseudo pour l'affichage public."); return; }
    if (form.pseudo.trim() && !isValidPseudo(form.pseudo.trim())) { setPseudoErr("Le pseudo doit faire 3–32 caractères (A–Z, a–z, 0–9, _, ., -)."); return; }
    if (!userId) return;

    setSaving(true);
    try {
      let photo_url = form.photo_url;

      if (photoFile) {
        const ext = photoFile.name.split(".").pop();
        const filePath = `${userId}/avatar.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("avatars")
          .upload(filePath, photoFile, { upsert: true });
        if (upErr) throw upErr;
        photo_url = filePath;
      }

      const { error: upsertErr } = await supabase.from("user_profiles").upsert({
        id: userId,
        full_name: form.full_name.trim(),
        club: form.club.trim(),
        niveau: form.niveau,
        pseudo: form.pseudo.trim() || null,
        public_use_pseudo: form.public_use_pseudo,
        photo_url,
        phone: form.phone.trim() || null,
      });

      if (upsertErr) throw upsertErr;

      setForm((p) => ({ ...p, photo_url }));
      setPhotoFile(null);
      setPhotoPreview("");
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      if ((err as any).code === "23505" && String(err.message).toLowerCase().includes("pseudo")) {
        setPseudoErr("Ce pseudo est déjà pris. Choisis-en un autre.");
      } else {
        setError(err?.message || "Erreur lors de la sauvegarde.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword() {
    setPwdError("");
    setPwdSuccess(false);

    if (newPassword.length < 6) { setPwdError("Le mot de passe doit faire au moins 6 caractères."); return; }
    if (newPassword !== confirmPassword) { setPwdError("Les mots de passe ne correspondent pas."); return; }

    setPwdSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setNewPassword("");
      setConfirmPassword("");
      setPwdSuccess(true);
      setTimeout(() => setPwdSuccess(false), 3000);
    } catch (err: any) {
      setPwdError(err?.message || "Erreur lors du changement de mot de passe.");
    } finally {
      setPwdSaving(false);
    }
  }

  const avatarSrc = photoPreview || (form.photo_url ? getStorageUrl("avatars", form.photo_url) : "");
  const publicDisplayName =
    form.public_use_pseudo && form.pseudo.trim() ? form.pseudo.trim() : form.full_name.trim() || "—";
  const canSubmit =
    !!form.full_name.trim() &&
    !saving &&
    (!form.public_use_pseudo || (form.pseudo.trim().length >= 3 && isValidPseudo(form.pseudo.trim())));

  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6">
        <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement du profil…
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          <div className="inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{error}</div>
        </div>
      )}
      {success && (
        <div className="rounded-2xl border border-green-200 bg-green-50 dark:border-green-900/40 dark:bg-green-950/30 px-4 py-3 text-sm text-green-700 dark:text-green-300">
          <div className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />Profil mis à jour.</div>
        </div>
      )}

      {/* Photo */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
        <div className="font-semibold text-gray-900 dark:text-gray-100 mb-4">Photo de profil</div>
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden grid place-items-center shrink-0">
            {avatarSrc ? <img src={avatarSrc} alt="Avatar" className="w-full h-full object-cover" /> : <User className="h-8 w-8 text-gray-400" />}
          </div>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <Camera className="h-4 w-4" />
              {avatarSrc ? "Changer la photo" : "Ajouter une photo"}
            </button>
            {photoFile && <div className="text-xs text-gray-500 dark:text-gray-400">{photoFile.name}</div>}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
          </div>
        </div>
      </div>

      {/* Identité */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 space-y-4">
        <div className="font-semibold text-gray-900 dark:text-gray-100">Identité</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400">Nom complet *</label>
            <input value={form.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} placeholder="Prénom Nom"
              className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700" />
          </div>
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400">Club</label>
            <input value={form.club} onChange={(e) => setForm((p) => ({ ...p, club: e.target.value }))} placeholder="Nom du club"
              className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700" />
          </div>
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400">Niveau</label>
            <select value={form.niveau} onChange={(e) => setForm((p) => ({ ...p, niveau: e.target.value }))}
              className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700">
              <option value="">Sélectionner…</option>
              <option value="Départemental">Départemental</option>
              <option value="Régional">Régional</option>
              <option value="National">National</option>
              <option value="Elite">Elite</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400">Téléphone</label>
            <div className="mt-1 relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                placeholder="+33 6 00 00 00 00"
                className="w-full pl-9 pr-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Pseudo */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 space-y-4">
        <div className="font-semibold text-gray-900 dark:text-gray-100">Pseudo public</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400">Pseudo</label>
            <input value={form.pseudo} onChange={(e) => { setForm((p) => ({ ...p, pseudo: e.target.value })); setPseudoErr(""); }}
              placeholder="ex: coach_cyp" maxLength={32}
              className={cx(
                "mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700",
                pseudoErr ? "border-red-400 dark:border-red-600" : "border-gray-200 dark:border-gray-800"
              )} />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className={pseudoErr ? "text-red-600 dark:text-red-400" : "text-gray-400"}>{pseudoErr || "Lettres, chiffres, _ . - (3–32)"}</span>
              <span className="text-gray-400">{form.pseudo.length}/32</span>
            </div>
          </div>
          <div className="flex flex-col justify-center gap-2 md:pt-5">
            <label className="inline-flex items-center gap-3 cursor-pointer">
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, public_use_pseudo: !p.public_use_pseudo }))}
                className={cx(
                  "relative inline-flex h-6 w-11 items-center rounded-full transition shrink-0",
                  form.public_use_pseudo ? "bg-gray-900 dark:bg-white" : "bg-gray-300 dark:bg-gray-700"
                )}
              >
                <span className={cx("inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-900 transition", form.public_use_pseudo ? "translate-x-5" : "translate-x-1")} />
              </button>
              <span className="text-sm text-gray-900 dark:text-gray-100">Afficher mon pseudo au lieu de mon nom</span>
            </label>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Nom public : <span className="font-medium">{publicDisplayName}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Save profile */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSubmit}
          className={cx(
            "inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm",
            "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
            !canSubmit && "opacity-50 cursor-not-allowed"
          )}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Sauvegarde…" : "Sauvegarder le profil"}
        </button>
      </div>

      {/* ── Connexion ─────────────────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 space-y-4">
        <div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
          <Lock className="h-4 w-4 text-gray-500" />
          Connexion
        </div>

        {/* Email — read only */}
        <div>
          <label className="text-xs text-gray-500 dark:text-gray-400">Adresse e-mail</label>
          <div className="mt-1 relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              value={userEmail}
              readOnly
              className="w-full pl-9 pr-3 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400 cursor-not-allowed"
            />
          </div>
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Pour modifier votre e-mail, contactez le support.</p>
        </div>

        {/* Change password */}
        <div className="pt-1 border-t border-gray-100 dark:border-gray-800 space-y-3">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Changer le mot de passe</div>

          {pwdError && (
            <div className="rounded-xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700 dark:text-red-300 inline-flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />{pwdError}
            </div>
          )}
          {pwdSuccess && (
            <div className="rounded-xl border border-green-200 bg-green-50 dark:border-green-900/40 dark:bg-green-950/30 px-3 py-2 text-sm text-green-700 dark:text-green-300 inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 shrink-0" />Mot de passe mis à jour.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400">Nouveau mot de passe</label>
              <div className="mt-1 relative">
                <input
                  type={showNewPwd ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setPwdError(""); }}
                  placeholder="Min. 6 caractères"
                  className="w-full pr-10 pl-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  tabIndex={-1}
                >
                  {showNewPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400">Confirmer le mot de passe</label>
              <div className="mt-1 relative">
                <input
                  type={showConfirmPwd ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setPwdError(""); }}
                  placeholder="Répéter le mot de passe"
                  className={cx(
                    "w-full pr-10 pl-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700",
                    confirmPassword && newPassword !== confirmPassword
                      ? "border-red-400 dark:border-red-600"
                      : "border-gray-200 dark:border-gray-800"
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  tabIndex={-1}
                >
                  {showConfirmPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">Les mots de passe ne correspondent pas.</p>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleChangePassword}
              disabled={pwdSaving || !newPassword || newPassword !== confirmPassword}
              className={cx(
                "inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm",
                "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
                (pwdSaving || !newPassword || newPassword !== confirmPassword) && "opacity-50 cursor-not-allowed"
              )}
            >
              {pwdSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              {pwdSaving ? "Mise à jour…" : "Changer le mot de passe"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ClubSettings() {
  const { clubId } = useParams<{ clubId: string }>();

  const [club, setClub] = useState<Club | null>(null);
  const [clubForm, setClubForm] = useState<Club | null>(null);
  const [myRole, setMyRole] = useState<string | null>(null);
  const [settings, setSettings] = useState<ClubSettingsState>(DEFAULT_SETTINGS);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState("");
  const logoInputRef = useRef<HTMLInputElement>(null);

  const [loadingPage, setLoadingPage] = useState(true);
  const [pageError, setPageError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");

  const [tab, setTab] = useState<TabKey>("general");

  const canEdit = !!myRole && EDITABLE_ROLES.includes(myRole);

  useEffect(() => {
    if (!clubId) return;
    let cancelled = false;

    async function load() {
      try {
        setLoadingPage(true);
        setPageError("");

        const { data: { user }, error: authErr } = await supabase.auth.getUser();
        if (authErr) throw authErr;
        if (!user) throw new Error("Non connecté.");

        const [clubRes, memberRes, settingsRes] = await Promise.all([
          supabase
            .from("clubs")
            .select("id, name, city, country, description, logo_path, primary_color, is_premium")
            .eq("id", clubId)
            .maybeSingle(),
          supabase
            .from("club_members")
            .select("role")
            .eq("club_id", clubId)
            .eq("user_id", user.id)
            .maybeSingle(),
          supabase
            .from("club_settings")
            .select("*")
            .eq("club_id", clubId)
            .maybeSingle(),
        ]);

        if (clubRes.error) throw clubRes.error;
        if (!clubRes.data) throw new Error("Club introuvable.");

        if (!cancelled) {
          const c = clubRes.data as Club;
          setClub(c);
          setClubForm(c);
          setMyRole(memberRes.data?.role ?? null);

          if (settingsRes.data) {
            setSettings({
              coach_can_invite: settingsRes.data.coach_can_invite ?? false,
              player_can_see_others_stats: settingsRes.data.player_can_see_others_stats ?? true,
              parent_can_see_team_stats: settingsRes.data.parent_can_see_team_stats ?? true,
              player_can_post: settingsRes.data.player_can_post ?? false,
              posts_allow_coaches: settingsRes.data.posts_allow_coaches ?? true,
              posts_allow_players: settingsRes.data.posts_allow_players ?? false,
              posts_allow_parents: settingsRes.data.posts_allow_parents ?? false,
            });
          }
        }
      } catch (err: any) {
        if (!cancelled) setPageError(err?.message || "Erreur de chargement.");
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [clubId]);

  const tabs = useMemo(
    () => [
      { key: "general" as const, label: "Général", icon: <Building2 className="h-4 w-4" /> },
      { key: "permissions" as const, label: "Permissions", icon: <Shield className="h-4 w-4" /> },
      { key: "posts" as const, label: "Publications", icon: <Megaphone className="h-4 w-4" /> },
      { key: "billing" as const, label: "Abonnement", icon: <Crown className="h-4 w-4" /> },
      { key: "data" as const, label: "Données", icon: <Database className="h-4 w-4" /> },
      { key: "notifications" as const, label: "Notifications", icon: <Bell className="h-4 w-4" /> },
      { key: "profil" as const, label: "Mon profil", icon: <User className="h-4 w-4" /> },
    ] as const,
    []
  );

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  }

  async function handleSave() {
    if (!canEdit || !clubId || !clubForm) return;

    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);

    try {
      let logo_path = clubForm.logo_path;
      if (logoFile) {
        const ext = logoFile.name.split(".").pop();
        const filePath = `${clubId}/logo.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("club-logos")
          .upload(filePath, logoFile, { upsert: true });
        if (upErr) throw upErr;
        logo_path = filePath;
      }

      const { error: clubErr } = await supabase
        .from("clubs")
        .update({
          name: clubForm.name,
          city: clubForm.city,
          country: clubForm.country,
          description: clubForm.description,
          logo_path,
          primary_color: clubForm.primary_color,
        })
        .eq("id", clubId);

      if (clubErr) throw clubErr;

      const { error: settingsErr } = await supabase
        .from("club_settings")
        .upsert({ club_id: clubId, ...settings, updated_at: new Date().toISOString() });

      if (settingsErr) throw settingsErr;

      setClub((p) => (p ? { ...p, ...clubForm, logo_path } : p));
      setLogoFile(null);
      setLogoPreview("");
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(err?.message || "Erreur lors de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  }

  async function exportMembersCSV() {
    if (!clubId) return;
    const { data, error } = await supabase
      .from("club_members")
      .select("email, role, status, created_at")
      .eq("club_id", clubId);

    if (error || !data) { alert("Erreur d'export : " + error?.message); return; }

    const header = "Email,Rôle,Statut,Date d'inscription\n";
    const rows = data.map((m) => `${m.email},${m.role},${m.status},${m.created_at}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `membres-club-${clubId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loadingPage) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6">
        <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement…
        </div>
      </div>
    );
  }

  if (pageError || !club || !clubForm) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-300">
        <div className="inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {pageError || "Club introuvable."}
        </div>
      </div>
    );
  }

  const logoSrc = logoPreview || (club.logo_path ? getStorageUrl("club-logos", club.logo_path) : "");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col items-center text-center gap-3 sm:flex-row sm:items-start sm:justify-between sm:text-left">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Configuration</div>
          <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center justify-center gap-2 sm:justify-start">
            <Settings className="h-5 w-5 text-gray-500" />
            Paramètres du club
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {club.name} · Identité, permissions, publications.
          </div>
        </div>

        {canEdit && tab !== "profil" && tab !== "notifications" && (
          <div className="flex items-center gap-2 shrink-0">
            {saveSuccess && (
              <span className="inline-flex items-center gap-1 text-sm text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                Sauvegardé
              </span>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className={cx(
                "inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm",
                "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
                saving && "opacity-60 cursor-not-allowed"
              )}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Sauvegarde…" : "Sauvegarder"}
            </button>
          </div>
        )}
      </div>

      {saveError && (
        <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          <div className="inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{saveError}</div>
        </div>
      )}

      {!canEdit && tab !== "profil" && tab !== "notifications" && (
        <div className="rounded-2xl bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-900/40 p-4 text-sm text-yellow-900 dark:text-yellow-200">
          Tu peux consulter les paramètres, mais seuls les dirigeants et cadres techniques peuvent les modifier.
        </div>
      )}

      {/* Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        {/* Tabs sidebar */}
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-2">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cx(
                "w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition",
                tab === t.key
                  ? "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100"
                  : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              )}
            >
              <span className="text-gray-600 dark:text-gray-300">{t.icon}</span>
              <span className="truncate">{t.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="lg:col-span-3 space-y-3">
          {tab === "profil" && <ProfileTab />}

          {tab === "notifications" && (
            <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Notifications</div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Gère tes notifications pour ce club et tes équipes sur cet appareil.
              </div>
              <NotificationPreferencesPanel />
            </div>
          )}

          {tab === "general" && (
            <div className="space-y-3">
              {/* Logo */}
              <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-4">Logo du club</div>
                <div className="flex items-center gap-4">
                  <div className="h-20 w-20 rounded-2xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden grid place-items-center shrink-0">
                    {logoSrc ? <img src={logoSrc} alt="Logo" className="w-full h-full object-cover" /> : <Building2 className="h-8 w-8 text-gray-400" />}
                  </div>
                  <div className="space-y-2">
                    <button
                      type="button"
                      disabled={!canEdit}
                      onClick={() => logoInputRef.current?.click()}
                      className={cx(
                        "inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100",
                        !canEdit && "opacity-60 cursor-not-allowed"
                      )}
                    >
                      <Upload className="h-4 w-4" />
                      {logoSrc ? "Changer le logo" : "Ajouter un logo"}
                    </button>
                    {logoFile && <div className="text-xs text-gray-500 dark:text-gray-400">{logoFile.name}</div>}
                    <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                  </div>
                </div>
              </div>

              {/* Club info */}
              <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 space-y-4">
                <div className="font-semibold text-gray-900 dark:text-gray-100">Informations du club</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Nom</label>
                    <input value={clubForm.name ?? ""} disabled={!canEdit}
                      onChange={(e) => setClubForm((p) => p ? { ...p, name: e.target.value } : p)}
                      className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700 disabled:opacity-60" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Ville</label>
                    <input value={clubForm.city ?? ""} disabled={!canEdit}
                      onChange={(e) => setClubForm((p) => p ? { ...p, city: e.target.value } : p)}
                      className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700 disabled:opacity-60" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Pays</label>
                    <input value={clubForm.country ?? ""} disabled={!canEdit}
                      onChange={(e) => setClubForm((p) => p ? { ...p, country: e.target.value } : p)}
                      className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700 disabled:opacity-60" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Couleur principale</label>
                    <div className="mt-1 flex gap-2">
                      <input type="color" value={clubForm.primary_color ?? "#111827"} disabled={!canEdit}
                        onChange={(e) => setClubForm((p) => p ? { ...p, primary_color: e.target.value } : p)}
                        className="h-9 w-14 rounded-xl border border-gray-200 dark:border-gray-800 cursor-pointer disabled:opacity-60" />
                      <input value={clubForm.primary_color ?? "#111827"} disabled={!canEdit}
                        onChange={(e) => setClubForm((p) => p ? { ...p, primary_color: e.target.value } : p)}
                        placeholder="#111827"
                        className="flex-1 px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700 disabled:opacity-60" />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs text-gray-500 dark:text-gray-400">Description</label>
                    <textarea rows={3} value={clubForm.description ?? ""} disabled={!canEdit}
                      onChange={(e) => setClubForm((p) => p ? { ...p, description: e.target.value } : p)}
                      className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700 disabled:opacity-60" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "permissions" && (
            <div className="space-y-3">
              <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
                <div className="font-semibold text-gray-900 dark:text-gray-100">Permissions</div>
                <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">Définit ce que chaque rôle peut faire dans le club.</div>
              </div>
              <Toggle label="Les coachs peuvent inviter des membres" desc="Permet aux coachs d'envoyer des invitations (joueurs/parents)."
                value={settings.coach_can_invite} onChange={(v) => setSettings((p) => ({ ...p, coach_can_invite: v }))} disabled={!canEdit} />
              <Toggle label="Les joueurs peuvent voir les stats des autres joueurs" desc="Si désactivé, un joueur ne voit que ses propres stats."
                value={settings.player_can_see_others_stats} onChange={(v) => setSettings((p) => ({ ...p, player_can_see_others_stats: v }))} disabled={!canEdit} />
              <Toggle label="Les parents peuvent voir les stats de l'équipe" desc="Sinon, ils ne verront que les stats de leurs enfants."
                value={settings.parent_can_see_team_stats} onChange={(v) => setSettings((p) => ({ ...p, parent_can_see_team_stats: v }))} disabled={!canEdit} />
              <Toggle label="Les joueurs peuvent publier sur le mur" desc="Sinon, seuls les coachs/admin publient."
                value={settings.player_can_post} onChange={(v) => setSettings((p) => ({ ...p, player_can_post: v }))} disabled={!canEdit} />
            </div>
          )}

          {tab === "posts" && (
            <div className="space-y-3">
              <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
                <div className="font-semibold text-gray-900 dark:text-gray-100">Publications</div>
                <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">Contrôle qui peut publier sur le mur du club.</div>
              </div>
              <Toggle label="Autoriser les coachs à publier"
                value={settings.posts_allow_coaches} onChange={(v) => setSettings((p) => ({ ...p, posts_allow_coaches: v }))} disabled={!canEdit} />
              <Toggle label="Autoriser les joueurs à publier"
                value={settings.posts_allow_players} onChange={(v) => setSettings((p) => ({ ...p, posts_allow_players: v }))} disabled={!canEdit} />
              <Toggle label="Autoriser les parents à publier"
                value={settings.posts_allow_parents} onChange={(v) => setSettings((p) => ({ ...p, posts_allow_parents: v }))} disabled={!canEdit} />
            </div>
          )}

          {tab === "billing" && (
            <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 space-y-4">
              <div className="font-semibold text-gray-900 dark:text-gray-100">Abonnement</div>
              <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Plan actuel</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {club.is_premium ? "Club Premium" : "Gratuit"}
                    </div>
                  </div>
                  {club.is_premium && (
                    <span className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-yellow-100 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-200">
                      <Crown className="h-4 w-4" />Actif
                    </span>
                  )}
                </div>
                <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  Gestion d'abonnement via portail Stripe (disponible prochainement).
                </div>
                <div className="mt-3">
                  <button type="button" disabled={!canEdit}
                    className={cx("inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100", !canEdit && "opacity-60 cursor-not-allowed")}>
                    <Crown className="h-4 w-4" />Gérer l'abonnement
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === "data" && (
            <div className="space-y-3">
              <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
                <div className="font-semibold text-gray-900 dark:text-gray-100">Données</div>
                <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">Exports et actions sensibles.</div>
              </div>

              <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Exports</div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={exportMembersCSV}
                    className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800">
                    Export membres (CSV)
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/10 p-4">
                <div className="font-semibold text-red-800 dark:text-red-200">Zone dangereuse</div>
                <div className="mt-1 text-sm text-red-700 dark:text-red-200">
                  Supprimer le club effacera définitivement toutes ses données.
                </div>
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() => {
                    if (window.confirm(`Supprimer définitivement le club "${club.name}" ? Cette action est irréversible.`)) {
                      alert("Suppression non implémentée — contacter le support.");
                    }
                  }}
                  className={cx(
                    "mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm",
                    "border border-red-200 dark:border-red-900/40 bg-white/60 dark:bg-red-900/10 text-red-800 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/20",
                    !canEdit && "opacity-60 cursor-not-allowed"
                  )}
                >
                  <Trash2 className="h-4 w-4" />
                  Supprimer le club
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
